import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../core/authContext';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { ActionFooter, PendingFooter, ReportCard } from './components/ReportCard';
import { ProgramEvaluationFooter } from './components/ProgramEvaluationFooter';
import {
  fetchLastGeneratedAt,
  formatLastGenerated,
  useLastGenerated,
  type LastGeneratedSource,
} from './lastGenerated';
import { programEvaluationCardView } from './programEvaluationCard';
import {
  fetchProgramEvaluationStatus,
  useProgramEvaluationStatus,
  type PeStatusSource,
} from './programEvaluationStatus';
import { catalogForRole, IMPLEMENTATION_KEY, LIBRARY_KEY, PROGRAM_EVALUATION_KEY, REPORT_CATALOG, STUDENT_PROGRESS_KEY } from './reportCatalog';
import { REPORTS } from './tokens';
import { WelcomeBanner } from './components/WelcomeBanner';
import { accountWelcome, useWelcomeBanner, type WelcomeSource } from './welcomeBanner';
import { serverSourcedSubmission } from '../ProgramEvaluation/dataStatus';
import { useProgramEvaluationGeneration } from '../ProgramEvaluation/generation-hook';
import { SAMPLE_PROGRAM_EVALUATION_ROUTE } from '../Samples/routes';
import { serverSampleGateSources, useSampleGate, type SampleGateSources } from '../Samples/sampleGate';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

const CARD_MIN_WIDTH = 400;
const PROGRAM_EVALUATION_ROUTE = '/program-evaluation';
const noProgramEvaluation: LastGeneratedSource = () => Promise.resolve(null);

export function ReportsPage({
  lastGeneratedSource = fetchLastGeneratedAt,
  welcomeSource = accountWelcome,
  gateSources = serverSampleGateSources,
  statusSource = fetchProgramEvaluationStatus,
}: {
  lastGeneratedSource?: LastGeneratedSource;
  welcomeSource?: WelcomeSource;
  gateSources?: SampleGateSources;
  statusSource?: PeStatusSource;
}) {
  const navigate = useNavigate();
  const { isSuperAdmin, isDistrictAdmin, impersonation, hasFeature } = useAuth();
  const gate = useSampleGate({ isSuperAdmin, isDistrictAdmin }, gateSources);
  const catalog = catalogForRole(REPORT_CATALOG, { isSuperAdmin, isDistrictAdmin }, hasFeature);
  const { lastGeneratedAt, loading } = useLastGenerated(
    isSuperAdmin ? lastGeneratedSource : noProgramEvaluation,
  );
  const districtEvaluation = !isSuperAdmin && isDistrictAdmin;
  const evaluationReady = districtEvaluation && gate.programEvaluation === 'present';
  const evaluation = useProgramEvaluationStatus(statusSource, evaluationReady);
  const runningYear = evaluation.status?.status === 'generating' ? evaluation.status.schoolYear : null;
  const generation = useProgramEvaluationGeneration({
    inputsProvider: serverSourcedSubmission,
    enabled: evaluationReady && evaluation.status !== null,
    schoolYear: runningYear ?? gate.schoolYear ?? evaluation.status?.schoolYear ?? undefined,
    unitMode: evaluation.status?.unitMode ?? undefined,
    onReady: evaluation.refetch,
  });
  const { refetch: refetchEvaluation } = evaluation;
  const runStarted = generation.phase === 'generating';
  const pollGaveUp = generation.pollExhausted;
  useEffect(() => {
    if (evaluationReady && (runStarted || pollGaveUp)) refetchEvaluation();
  }, [evaluationReady, runStarted, pollGaveUp, refetchEvaluation]);
  const cardView = programEvaluationCardView(
    gate.programEvaluation,
    evaluation.status,
    generation,
    evaluation.givenUp,
    gate.schoolYear,
  );
  const retryGeneration = () => {
    const failedPhase = generation.phase === 'failed' || generation.phase === 'error';
    if (!failedPhase) {
      generation.regenerate();
      return;
    }
    const resumedFailure = generation.phase === 'failed' && !generation.freshJob;
    const freshnessUnknown =
      evaluation.status === null ||
      evaluation.status.stale ||
      evaluation.status.status === 'none' ||
      generation.jobUpdatedAt === null;
    if (resumedFailure && freshnessUnknown) {
      generation.regenerate();
      return;
    }
    generation.retry();
  };
  const welcome = useWelcomeBanner(welcomeSource, { enabled: !isSuperAdmin, persist: !impersonation });
  const columnRef = useRef<HTMLDivElement>(null);
  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: REPORTS.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, padding: '32px 24px' }}>
        <Box
          ref={columnRef}
          sx={{
            maxWidth: PAGE_MAX_WIDTH,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {welcome.visible && (
            <Box sx={{ marginBottom: '8px' }}>
              <WelcomeBanner
                onDismiss={() => {
                  welcome.dismiss();
                  columnRef.current?.querySelector<HTMLElement>('[data-testid="report-row"] button')?.focus();
                }}
              />
            </Box>
          )}
          <Box
            data-testid="report-row"
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
              gap: '16px',
            }}
          >
            {catalog.map((report) => (
              <ReportCard
                key={report.key}
                title={report.title}
                description={report.description}
                coverSrc={report.coverSrc}
                footer={
                  report.key === STUDENT_PROGRESS_KEY ? (
                    <ActionFooter
                      lastGenerated={null}
                      actionLabel="View"
                      variant="outlined"
                      onAction={() => navigate('/reports/student-progress')}
                    />
                  ) : report.key === IMPLEMENTATION_KEY ? (
                    <ActionFooter
                      lastGenerated={null}
                      actionLabel="Open"
                      actionName="Open Implementation report"
                      variant="outlined"
                      onAction={() => navigate('/reports/implementation')}
                    />
                  ) : report.key === LIBRARY_KEY ? (
                    <ActionFooter
                      lastGenerated={null}
                      actionLabel="Open"
                      actionName="Open Assignment library"
                      variant="outlined"
                      onAction={() => navigate('/reports/library')}
                    />
                  ) : report.key === PROGRAM_EVALUATION_KEY && districtEvaluation ? (
                    <ProgramEvaluationFooter
                      view={cardView}
                      onPreview={() => navigate(SAMPLE_PROGRAM_EVALUATION_ROUTE)}
                      onGenerate={generation.submit}
                      onRetry={retryGeneration}
                      onRegenerate={generation.regenerate}
                      onView={() => navigate(PROGRAM_EVALUATION_ROUTE)}
                      onRetryStatus={evaluation.refetch}
                    />
                  ) : loading ? (
                    <PendingFooter />
                  ) : (
                    <ActionFooter
                      lastGenerated={
                        lastGeneratedAt === null ? null : formatLastGenerated(lastGeneratedAt)
                      }
                      actionLabel={lastGeneratedAt === null ? 'Generate' : 'View'}
                      variant={lastGeneratedAt === null ? 'contained' : 'outlined'}
                      onAction={() => navigate(PROGRAM_EVALUATION_ROUTE)}
                    />
                  )
                }
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
